// СТРОКИ ФИНАЛА ДЕКИ (ШАГ 3) — склейка римских, ед./мн. ветки и корпус экрана `done()`. B5-1.
//
// ИСТОЧНИК — `done()` борда `Life Map - s3 Deck v8.html` (`:644–665`), С ТРЕМЯ ПОПРАВКАМИ ПО
// ТАБЛИЦЕ ОТСТАВАНИЙ `docs/design/WIZARD-CANON.md`. Это главное, что надо знать про этот файл:
// сырой байт борда здесь — НЕВЕРНЫЙ эталон, и порт «как в борде» запёк бы в код три известных
// долга с зелёным тестом.
//
//   1. `s506.d2` — склейка римских. Борд делает `join(', ')` и печатает `Cards I, II, III have`;
//      решение требует терминальное «and» БЕЗ оксфордской запятой: `Cards I, II and III have`.
//      Жирным идёт весь список целиком, вместе с «and».
//   2. `s824.d4` — микро-строка под CTA. Борд печатает `next · the layered map · шаг 4`:
//      кириллица на экране англоязычного продукта плюс копи, перекрытая решением. Канон —
//      `next · the same life`, номер шага не возвращается (`s507.d3` снял его отдельно).
//   3. `s711.d2` — апостроф. Долг касается Т-ярлыка `didn't allow yourself` (дом — `prompts.js`),
//      а не корпуса деки; в самом финале апострофов нет вовсе. Названо здесь, потому что
//      отрицательное утверждение о нём стоит в приёмке и обязано быть проверяемым.
//
// ⛔ ЧЕГО ЗДЕСЬ НЕТ. Надписи открытого CTA (`see all seven layers`) и кнопки назад
// (`← last card`) — это футер, его пять состояний и четыре back живут в своём тикете B5-N. Здесь
// только то, что печатает тело финала, плюс надпись ЗАКРЫТОГО состояния: она несёт счёт карточек,
// то есть принадлежит арифметике гейта, а не таблице навигации (ровно как `ctaGate` шага 2).

import { roman } from '../../roman.js';

/** Неразрывный пробел (U+00A0) — `&nbsp;` борда вокруг числа в закрытой надписи CTA. */
const NB = '\u00A0';

/**
 * Склейка списка через терминальное «and» без оксфордской запятой (`s506.d2`).
 *
 * ⚠️ БОРД ДЕЛАЕТ НЕ ТАК, И ЭТО НАЗВАННОЕ ОТСТАВАНИЕ. У него `join(', ')` — `I, II, III`. Борды
 * задним числом не переиздаются (`s518.d2`), строка доезжает сборкой; расхождение стоит в таблице
 * отставаний реестра и пришпилено копи-парити с обеих сторон.
 *
 * Два элемента — «and» без запятой вовсе (`I and II`): запятая перед «and» и есть оксфордская.
 */
export function joinAnd(parts) {
  const list = (parts || []).filter((p) => p !== null && p !== undefined && p !== '');
  if (list.length === 0) return '';
  if (list.length === 1) return String(list[0]);
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/** `{ROMAN_LIST}` — индексы карточек в римские номера и склейка по `s506.d2`. */
export function romanList(indexes) {
  return joinAnd((indexes || []).map((i) => roman(i)));
}

/**
 * Неизменная часть финала — надстрочник, лид и два заголовка блоков (борд `:657–661`, `:651`,
 * `:654`).
 *
 * Заголовки лежат здесь, а не в функциях блоков, по той же причине, что у борда: блок — это
 * заголовок ПЛЮС строка, и функция, отдающая тело без заголовка, отдаёт половину сообщения.
 */
export const DONE_COPY = {
  eyebrow: 'the deck is read',
  /** `All {N} stages,` + `from the inside.` — второй половиной борд красит `inside` янтарём. */
  big: (n) => ({ count: `All ${n} stages,`, tail: 'from the ', accent: 'inside', end: '.' }),
  lead: 'That pale line is one average of seven. Underneath it there are seven separate lines, '
    + 'and they do not agree with each other.',
  leadQuiet: 'That disagreement is where your reading starts.',
  hardHeading: 'before the map is drawn',
  softHeading: 'if there is more to say',
};

/**
 * Микро-строка под CTA финала (`s3.done.foot`).
 *
 * ⭐ ЭТО СТРОКА РЕШЕНИЯ (`s824.d4`), А НЕ ПОРТ БАЙТА. Борд печатает `next · <b>the layered
 * map</b> · шаг 4`; канон — `next · the same life`, и номер шага не возвращается. Форма узла —
 * борда: слово `next`, разделитель, жирный хвост.
 */
export const doneFoot = () => ({ lead: 'next · ', bold: 'the same life' });

/**
 * Жёсткий блок — стена (борд `:651–653`).
 *
 * СУЩЕСТВИТЕЛЬНОЕ — КАРТОЧКА, и это не стилистика: `hard` говорит про карточки, `soft` — про
 * точки на линии. Своди их к одному слову — и два блока начнут читаться как один, ровно то, из-за
 * чего подписан вариант B.
 *
 * `lead`/`bold`/`trail` — половинки вокруг списка: у борда список стоит отдельным узлом `<b>`, и
 * склеенная строка потеряла бы единственное, что в этом абзаце выделено.
 *
 * @param {number[]} hard индексы карточек под полом
 * @returns {null|{lead: string, bold: string, trail: string}} `null` = стены нет
 */
export function hardBody(hard) {
  if (!hard || hard.length === 0) return null;
  const one = hard.length === 1;
  return {
    lead: one ? 'Card ' : 'Cards ',
    bold: romanList(hard),
    // «two» — словом, как в борде. Число здесь не подставляется из `FLOOR` намеренно: это часть
    // ФРАЗЫ, а не счётчик, и у неё нет ветки на другое значение пола. Что слово и пол не
    // разъехались, держит отдельное утверждение теста, а не шаблон с недостижимой веткой.
    trail: `${one ? ' has' : ' have'} fewer than two layers moved. `
      + `The line has nothing to run through ${one ? 'it' : 'them'}. Tap to go back.`,
  };
}

/**
 * Надпись ЗАКРЫТОГО CTA финала (борд `:664`).
 *
 * Число у борда — отдельный узел (`<span class="n">`) и единственный насыщенный пигмент
 * состояния, поэтому отдаётся половинками, как `ctaGate` шага 2.
 *
 * ⚠️ ПРОБЕЛЫ ВОКРУГ ЧИСЛА — НЕРАЗРЫВНЫЕ (U+00A0), как `&nbsp;` борда, и это не мелочь: число в
 * этой надписи стоит отдельным узлом, и обычный пробел позволил бы переносу оставить его одно в
 * строке. Константа названа именем (`NB`), а не вписана невидимым символом в три литерала: иначе
 * следующий, кто наберёт строку с клавиатуры, разойдётся с ней и не увидит, чем.
 *
 * @returns {null|{label: string, count: number, lead: string, trail: string}} `null` = гейт открыт
 */
export function hardCta(hard) {
  if (!hard || hard.length === 0) return null;
  const n = hard.length;
  const noun = n === 1 ? 'card' : 'cards';
  return {
    label: `finish${NB}${n}${NB}${noun} to continue`,
    count: n,
    lead: `finish${NB}`,
    trail: `${NB}${noun} to continue`,
  };
}

/**
 * Тихий блок (борд `:654–656`, копи `s497.d1`).
 *
 * СЧЁТ ИДЁТ ПО ПОЛЗУНКАМ, а не по карточкам: «точка стоит на двух слоях из семи». Обещания
 * «больше линий» и «можно будет сравнить» сняты намеренно — на этом экране линия одна, среднее
 * семи, и добор слоёв новых линий не даёт; семь отдельных линий живут на шаге 4.
 *
 * @param {number[]} soft индексы карточек ровно на полу
 * @returns {null|{body: string}} `null` = блока нет
 */
export function softBody(soft) {
  if (!soft || soft.length === 0) return null;
  const one = soft.length === 1;
  return {
    body: `${one ? 'This point runs' : 'These points run'} through two layers of seven. `
      + `The map is drawn either way. A third layer moves ${one ? 'it' : 'each'}.`,
  };
}

/** Надпись кнопки-номера в списке блока: `I · the first year` (борд `:650`). */
export function jumpLabel(index, stage) {
  return `${roman(index)} · ${stage && stage.name ? stage.name : ''}`.trim();
}
